import * as THREE from 'three';

// Rich palette inspired by warm, lush fantasy voxel landscapes (Shire / Hobbiton)
export const PALETTE = {
  // Grass & Moss - Deep, warm lush greens
  GRASS_BRIGHT: 0x58992e,
  GRASS_LUSH: 0x427d22,
  GRASS_DARK: 0x336118,
  GRASS_DRY: 0x6e9a38,
  MOSS: 0x3b661d,

  // Dirt & Paths - Warm ochre, golden sand, rich soil
  DIRT_LIGHT: 0xba8c53,
  DIRT_MEDIUM: 0x9e713b,
  DIRT_DARK: 0x7a5225,
  PATH_SAND: 0xd2aa6d,

  // Stone & Rock - Warm slate, cobbles, weathered granite
  STONE_LIGHT: 0x8a8e94,
  STONE_MEDIUM: 0x666a70,
  STONE_DARK: 0x45484d,
  SLATE_ROOF: 0x33363b,
  COBBLE: 0x757a82,

  // Wood & Timber - Rich warm oak and dark timber
  WOOD_OAK_LIGHT: 0x9c7247,
  WOOD_OAK_MEDIUM: 0x734f2d,
  WOOD_OAK_DARK: 0x4a3017,
  WOOD_PINE_BARK: 0x3d2714,
  WOOD_LOG_END: 0xba9263,

  // Foliage - Lush oak, pine, golden autumn
  LEAF_OAK_LUSH: 0x3a7822,
  LEAF_OAK_LIGHT: 0x4f9430,
  LEAF_PINE_DARK: 0x214a1e,
  LEAF_AUTUMN_GOLD: 0xd69622,
  LEAF_AUTUMN_RED: 0xb33a1e,
  LEAF_WILLOW: 0x5e9942,

  // Roofs & Thatch - Golden straw thatch and terracotta clay
  THATCH_YELLOW: 0xd19d36,
  THATCH_DARK: 0x9c7021,
  ROOF_RED_CLAY: 0x993922,
  ROOF_DARK_WOOD: 0x3b2518,

  // Crops & Plants - Golden wheat, pumpkins, flowers
  WHEAT_GOLD: 0xdeb443,
  WHEAT_STEM: 0x9e9538,
  PUMPKIN_ORANGE: 0xee6713,
  PUMPKIN_DARK: 0xc44c06,
  PUMPKIN_STEM: 0x226e1c,
  FLOWER_RED: 0xeb2f2f,
  FLOWER_YELLOW: 0xf5d327,
  FLOWER_PURPLE: 0x983ae8,
  FLOWER_WHITE: 0xf2f2f2,
  FLOWER_BLUE: 0x338ce8,
  SUNFLOWER_CENTER: 0x4d320d,

  // Water & Glass & Light - Shimmering cyan river, warm glowing windows
  WATER_CYAN: 0x22c2b8,
  WATER_CYAN_SHALLOW: 0x3ee8de,
  WATER_CYAN_MID: 0x1db3a9,
  WATER_CYAN_DEEP: 0x0f8c83,
  WATER_FOAM: 0xcffcf8,
  GLASS_GLOW: 0xffb05b,
  LAMP_GOLD: 0xffb05b,
  GLASS_WINDOW: 0xaaddff,
  SMOKE_WHITE: 0xdddddd,
  FIRE_ORANGE: 0xff5500,
};

// Helper to add subtle natural color variance per voxel so instanced meshes look organic
export function getRandomizedColor(hexColor: number, varianceRange = 0.03): number {
  let range = varianceRange;
  if (range > 1.0) {
    range /= 100;
  }
  range = Math.min(range, 0.04);

  const color = new THREE.Color(hexColor);
  const hsl = { h: 0, s: 0, l: 0 };
  color.getHSL(hsl);

  // Slightly jitter lightness and hue
  hsl.l = Math.max(0.05, Math.min(0.95, hsl.l + (Math.random() - 0.5) * range));
  hsl.s = Math.max(0.05, Math.min(0.95, hsl.s + (Math.random() - 0.5) * (range * 0.5)));

  color.setHSL(hsl.h, hsl.s, hsl.l);
  return color.getHex();
}

export function hexToColor(hexColor: number | string): THREE.Color {
  return new THREE.Color(hexColor);
}
